'use client';

import { RefObject, useMemo } from 'react';
import gsap from 'gsap';
import { useIsomorphicLayoutEffect } from 'utils/useIsomorphicLayoutEffect';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { debounce } from 'utils/performance/debounce';
import { TABLET_MAX_WIDTH_IN_PX } from 'components/theme';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
  ScrollTrigger.config({ ignoreMobileResize: true });
}

const REFRESH_DEBOUNCE_MS = 300;
const COARSE_POINTER_QUERY = '(pointer: coarse)';

let resizeRetainCount = 0;
let lastWidth = 0;

const refreshOnWidthChange = debounce(() => {
  const width = window.innerWidth;
  const heightOnly =
    width === lastWidth &&
    (width <= TABLET_MAX_WIDTH_IN_PX ||
      window.matchMedia(COARSE_POINTER_QUERY).matches);
  lastWidth = width;
  if (heightOnly) return;
  ScrollTrigger.refresh();
}, REFRESH_DEBOUNCE_MS);

function retainResizeRefresh(): void {
  if (resizeRetainCount === 0) {
    lastWidth = window.innerWidth;
    window.addEventListener('resize', refreshOnWidthChange);
  }
  resizeRetainCount += 1;
}

function releaseResizeRefresh(): void {
  resizeRetainCount = Math.max(0, resizeRetainCount - 1);
  if (resizeRetainCount === 0) {
    window.removeEventListener('resize', refreshOnWidthChange);
  }
}

export function useGsapContext<T extends HTMLElement = HTMLDivElement>(
  scope?: RefObject<T | null>,
  context: gsap.ContextFunc = () => {},
) {
  const ctx = useMemo(() => gsap.context(context, scope), [scope]);

  useIsomorphicLayoutEffect(() => {
    retainResizeRefresh();
    return () => {
      releaseResizeRefresh();
      ctx.revert();
    };
  }, [ctx]);

  return ctx;
}
